/**
 * In-app product feedback: star rating + short form (bug / idea / question).
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useLanguage } from '../contexts/LanguageContext';
import { markFeedbackSubmitted } from '../utils/feedbackPrompt';
import './FeedbackForm.css';

const CATEGORIES = ['rating', 'idea', 'bug', 'question'];

const FeedbackForm = ({
  page = '',
  compact = false,
  plain = false,
  variant = 'default',
  onSent,
}) => {
  const { t } = useLanguage();
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [category, setCategory] = useState('rating');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const active = hover || rating;
  const needsMessage = category !== 'rating' || (rating > 0 && rating <= 3);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (rating < 1) {
      toast.error(t('feedback.needRating'));
      return;
    }
    if (needsMessage && message.trim().length < 5) {
      toast.error(t('feedback.needMessage'));
      return;
    }

    setSending(true);
    try {
      await axios.post('/api/cvs/feedback/', {
        rating,
        category,
        message: message.trim(),
        page: page || (typeof window !== 'undefined' ? window.location.pathname : ''),
      });
      setSent(true);
      markFeedbackSubmitted();
      toast.success(t('feedback.thanks'));
      if (onSent) {
        onSent();
      }
    } catch (error) {
      const data = error.response?.data;
      const msg =
        data?.error ||
        (error.response?.status === 429 ? t('feedback.rateLimited') : null) ||
        t('feedback.error');
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const className = [
    'feedback-form',
    compact ? 'is-compact' : '',
    plain ? 'is-plain' : '',
  ]
    .filter(Boolean)
    .join(' ');

  if (sent) {
    return (
      <section className={className} aria-live="polite">
        <p className="feedback-form__thanks">{t('feedback.thanksLong')}</p>
      </section>
    );
  }

  return (
    <section
      className={className}
      aria-labelledby="feedback-form-title"
    >
      <h2 id="feedback-form-title" className="feedback-form__title">
        {t(variant === 'afterDownload' ? 'feedback.afterDownloadTitle' : 'feedback.title')}
      </h2>
      <p className="feedback-form__lead">
        {t(variant === 'afterDownload' ? 'feedback.afterDownloadLead' : 'feedback.lead')}
      </p>

      <form className="feedback-form__body" onSubmit={handleSubmit}>
        <fieldset className="feedback-form__stars">
          <legend className="feedback-form__legend">{t('feedback.ratingLabel')}</legend>
          <div className="feedback-form__star-row" role="radiogroup" aria-label={t('feedback.ratingLabel')}>
            {[1, 2, 3, 4, 5].map((value) => {
              const selected = value <= active;
              return (
                <button
                  key={value}
                  type="button"
                  className={`feedback-form__star${selected ? ' is-on' : ''}`}
                  aria-label={t('feedback.starN').replace('{n}', String(value))}
                  aria-checked={rating === value}
                  role="radio"
                  onClick={() => setRating(value)}
                  onMouseEnter={() => setHover(value)}
                  onMouseLeave={() => setHover(0)}
                  onFocus={() => setHover(value)}
                  onBlur={() => setHover(0)}
                >
                  <span aria-hidden="true">★</span>
                </button>
              );
            })}
          </div>
        </fieldset>

        <fieldset className="feedback-form__cats">
          <legend className="feedback-form__legend">{t('feedback.categoryLabel')}</legend>
          <div className="feedback-form__cat-row" role="radiogroup">
            {CATEGORIES.map((id) => (
              <label key={id} className={`feedback-form__cat${category === id ? ' is-active' : ''}`}>
                <input
                  type="radio"
                  name="feedback-category"
                  value={id}
                  checked={category === id}
                  onChange={() => setCategory(id)}
                />
                {t(`feedback.cat.${id}`)}
              </label>
            ))}
          </div>
        </fieldset>

        <label className="feedback-form__message" htmlFor="feedback-message">
          {needsMessage ? t('feedback.messageRequired') : t('feedback.messageOptional')}
          <textarea
            id="feedback-message"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            rows={compact ? 3 : 4}
            maxLength={2000}
            placeholder={t('feedback.messagePlaceholder')}
          />
        </label>

        <button type="submit" className="btn btn-primary feedback-form__submit" disabled={sending || rating < 1}>
          {sending ? t('feedback.sending') : t('feedback.submit')}
        </button>
      </form>
    </section>
  );
};

FeedbackForm.propTypes = {
  page: PropTypes.string,
  compact: PropTypes.bool,
  plain: PropTypes.bool,
  variant: PropTypes.oneOf(['default', 'afterDownload']),
  onSent: PropTypes.func,
};

export default FeedbackForm;
